import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    CertificatesDocument,
    ICertificateManagementListItemFragment,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useCertificateFilter } from 'certificate/hooks/useCertificateFilter';

interface IOptions {
    /** Pass `false` to exclude external certificates, `true` to only show them. */
    external?: boolean;
}

export const useCertificateList = (
    filterParamsInUrl = true,
    { external }: IOptions = {}
) => {
    const { showApolloError } = useApolloError();

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        handleSearch: onSearch,
        handleFilterChange: onFilterChange,
        loading: loadingFilters,
    } = useCertificateFilter(filterParamsInUrl);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    // Build variables for query
    const variables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
            external,
        }),
        [paginationVariables, filterValuesForQuery, external]
    );

    const {
        loading: certificatesLoading,
        data: certificatesData,
        error: certificatesError,
    } = useQuery(CertificatesDocument, {
        variables,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (certificatesError) {
            showApolloError(certificatesError);
        }
    }, [certificatesError, showApolloError]);

    const certificatesObject = certificatesData?.certificates;
    const certificatesCount = certificatesObject?.count;
    const certificates = useMemo(
        () =>
            (certificatesObject?.edges
                ?.map((edge) => edge?.node || null)
                .filter(Boolean) as ICertificateManagementListItemFragment[]) ||
            [],
        [certificatesObject?.edges]
    );

    const loading = certificatesLoading || (loadingFilters && !filters.length);

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(certificatesCount || 0);
    }, [certificatesCount, loading, initializePagination]);

    return {
        certificates,
        certificatesCount,
        loading,
        paginationSettings,
        filters,
        filterValues,
        onSearch,
        onFilterChange,
        setPage,
    };
};
